/* eslint-disable max-lines */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { isMobile } from 'react-device-detect';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import PlayCircleFilled from '@material-ui/icons/PlayCircleFilled';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeMute from '@material-ui/icons/VolumeMute';
import VolumeUp from '@material-ui/icons/VolumeUp';
import Hls from 'hls.js';
import { backOff } from 'exponential-backoff';

import logger from 'logger';
import VideoAdService from 'services/adServices/VideoAdService';
import CookieStorageService from 'services/CookieStorageService';
import HlsService from 'services/Streaming/hlsService';
import { translate } from 'services/i18nService';
import track from 'services/tracking';
import {
  HLS_DEBUG,
  HLS_STREAMING_URL_FAILED,
  SEND_HLS_VIDEO_EVENTS,
} from 'services/MessagingService/EventNames';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import Button from 'components/Button/Button';
import LoadingDots from 'components/LoadingDots/LoadingDots';
import VideoMetrics from 'components/VideoMetrics/VideoMetrics';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { BROADCAST_STATUS, NEW_SHOWS_STATE } from 'enum/videoPlayer';
import { generateID, getSessionUuid, isMobileRender } from 'utils/helpers';

import DMCABadge from '../../../../images/DMCABadge.png';
import Background from '../Background';

import StreamQualityOptions from './Controls/StreamQualityOptions';
import SmartxPlayerMetrics from './SmartxPlayerMetrics';
import styles from './SmartxPlayer.css';


const SET_SCREEN_ORIENTATION_DELAY = 800;
const HIDE_ICONS_DELAY = 3 * SECOND_AS_MILLISECONDS;
const SEND_VIDEO_METRICS_DELAY = 10 * SECOND_AS_MILLISECONDS;
const UPDATE_VIDEO_METRICS_DELAY = 10 * SECOND_AS_MILLISECONDS;

const fullscreenEventTypes = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'msfullscreenchange',
];

@observer
class SmartxPlayer extends Component {
  static propTypes = {
    source: PropTypes.string.isRequired,
    autoPlay: PropTypes.bool,
    embedProfileV2: PropTypes.bool,
    forcePlayerResize: PropTypes.string,
    fullscreen: PropTypes.bool,
    handleVideoDoubleClick: PropTypes.func,
    height: PropTypes.string,
    isDesktop: PropTypes.bool,
    isSfw: PropTypes.bool,
    muted: PropTypes.bool,
    offFullScreen: PropTypes.func,
    offHover: PropTypes.func,
    profileName: PropTypes.string,
    reconnect: PropTypes.func,
    renderVideoAdElement: PropTypes.func,
    renderViewerControls: PropTypes.func,
    sendVideoMetrics: PropTypes.func,
    shouldShowVideoAds: PropTypes.bool,
    showControls: PropTypes.bool,
    showUnmuteOverlay: PropTypes.bool,
    startAutoPlay: PropTypes.func,
    temporaryMessagingService: PropTypes.object,
    temporaryPlayerService: PropTypes.object,
    updateVideoMetrics: PropTypes.func,
    upgradeToGold: PropTypes.func,
    user: PropTypes.object,
    width: PropTypes.string,
    onFullScreen: PropTypes.func,
    onHover: PropTypes.func,
  };

  static defaultProps = {
    autoPlay: true,
    embedProfileV2: false,
    forcePlayerResize: undefined,
    fullscreen: false,
    handleVideoDoubleClick: () => undefined,
    height: '100%',
    isDesktop: false,
    isSfw: false,
    muted: true,
    offFullScreen: () => undefined,
    offHover: () => undefined,
    profileName: undefined,
    reconnect: () => undefined,
    renderVideoAdElement: () => null,
    renderViewerControls: () => null,
    sendVideoMetrics: () => undefined,
    shouldShowVideoAds: false,
    showControls: false,
    showUnmuteOverlay: false,
    startAutoPlay: () => undefined,
    temporaryMessagingService: undefined,
    temporaryPlayerService: undefined,
    updateVideoMetrics: () => undefined,
    upgradeToGold: () => undefined,
    user: undefined,
    // source is mentioned as required, but this needs to be checked before the next line is to be removed
    source: 'https://cstr-vod.castr.io/vdafddac30977c11e9bdcb/71983cc5-f510-4b36-b77a-fc3d216e15d3/index.m3u8',
    width: '100%',
    onFullScreen: () => undefined,
    onHover: () => undefined,
  };

  constructor(...args) {
    super(...args);

    this.delayedSetVideoTimer = null;

    this.setVideoRef = (element) => {
      if (element) {
        this.video = element;
        this.props.temporaryPlayerService.setVideo(this.video);
      }
    };

    this.setVideoPlayerRef = React.createRef();

    this.metricsInterval = null;
    this.smartXMetrics = null;
    this.sendMetricsInterval = null;
    this.buttonsHideTimeout = null;
    this.sessionUuid = getSessionUuid(this.props.user);
    this.sessionStarted = new Date();

    this.IDs = {
      prefix: 'vyut8KF3',
      element: 'smartxPlayer',
      unmuteButtonDesktop: 'unmuteButtonDesktop',
    };
    this.makeId = generateID(this.IDs.prefix, this.IDs.element);
  }

  state = {
    showPip: false,
    videoHeight: 0,
    closePip: false,
    determinedVideoHeight: 0,
    hideVolume: false,
    forceRerender: false,
    previousScroll: 0,
    videoHeightForScroll: 0,
  };

  @observable video = null;

  componentDidMount() {
    const { isDesktop } = this.props;

    this.setVideo();

    if (isDesktop) {
      window.addEventListener('scroll', this.handlePip);
    }

    this.video.addEventListener('loadedmetadata', this.startVideoAds);
    this.handleVideoHeight();
    window.addEventListener('resize', this.handleVideoHeight);
    this.handleTrackMetrics();

    fullscreenEventTypes.forEach(
      (eventType) => document.addEventListener(eventType, this.handleFullscreenChange)
    );

    if (isDesktop) {
      const videoElement = document.querySelector('#videoInteractSectionWrapper');
      if (videoElement) {
        videoElement.addEventListener('resize', this.determineVideoHeight);
      }
    }
    this.setHideIconsTimeout();
  }

  componentDidUpdate(prevProps) {
    const { forcePlayerResize, fullscreen, source } = this.props;

    if (prevProps.source !== source) {
      this.setVideo();
    }
    if (fullscreen !== prevProps.fullscreen && fullscreen) {
      this.setVideoPlayerRef.current.scrollLeft =
        (this.setVideoPlayerRef.current.scrollWidth - this.setVideoPlayerRef.current.clientWidth) / 2;
    }

    if (prevProps.forcePlayerResize !== forcePlayerResize) {
      const videoInteractSectionWrapper = document.querySelector('#videoInteractSectionWrapper');
      if (videoInteractSectionWrapper?.offsetHeight) {
        this.handleVideoHeight();
      }
    }
  }

  componentWillUnmount() {
    const { isDesktop } = this.props;
    const videoElement = document.querySelector('#videoInteractSectionWrapper');

    clearTimeout(this.delayedSetVideoTimer);

    if ('orientation' in screen) {
      screen.orientation.removeEventListener('change', this.handleOrientationChange);
    }
    if (isDesktop) {
      window.removeEventListener('scroll', this.handlePip);
    }
    clearInterval(this.metricsInterval);
    clearInterval(this.sendMetricsInterval);
    clearTimeout(this.buttonsHideTimeout);
    this.smartXMetrics?.destructor?.();
    HlsService.destroy();
    VideoAdService.reset();
    this.video.removeEventListener('loadedmetadata', this.startVideoAds);
    window.removeEventListener('resize', this.handleVideoHeight);

    fullscreenEventTypes.forEach(
      (eventType) => document.removeEventListener(eventType, this.handleFullscreenChange)
    );

    if (videoElement) {
      videoElement.removeEventListener('resize', this.determineVideoHeight);
    }
  }

  handleVideoSwipe = () => {
    if (!CookieStorageService.get('swipe-animation-shown')) {
      CookieStorageService.set('swipe-animation-shown', 'shown', { path: '/' });
    }
  };

  handleFullscreenChange = () => {
    const { temporaryPlayerService } = this.props;
    const {
      setCustomFullscreen,
      setTheatreMode,
    } = ProfileStore;

    if (temporaryPlayerService.isInFullscreen()) {
      this.removeVideoMaxHeight();
      this.removeVideoHeight();
    } else {
      temporaryPlayerService.customFullscreenActive = false;
      setCustomFullscreen(false);
      setTheatreMode(false);
      this.determineVideoHeight();
    }
  };

  handleCustomVideoMetrics = () => {
    clearInterval(this.metricsInterval);
    this.metricsInterval = setInterval(() => {
      if (this.video.played.length && this.video.seekable.length) {
        const videoMetrics = {
          bfl:
            this.video.seekable.end(this.video.seekable.length - 1) -
            this.video.played.end(this.video.played.length - 1),
        };
        this.props.updateVideoMetrics(videoMetrics);
      }
    }, UPDATE_VIDEO_METRICS_DELAY);
  };

  checkIsVideoVisibleToEndUser = () => {
    const rect = this.video.getBoundingClientRect();
    const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);

    return !(rect.bottom < 0 || rect.top - viewHeight >= 0) && !document.hidden && document.hasFocus();
  };

  handleTrackMetrics = () => {
    const {
      profileName,
      sendVideoMetrics,
      temporaryPlayerService,
      user,
    } = this.props;

    const playerEl = this.setVideoPlayerRef.current;

    this.sendMetricsInterval =
      process.env.NODE_ENV === 'production' &&
      setInterval(() => {
        const metricsData = {
          metrics: {
            bitrate: temporaryPlayerService.videoMetrics.viewerBR,
            fps: temporaryPlayerService.videoMetrics.viewerFps,
            streamSource: FirebaseStore.streamSource,
            streamSize: `${this.video.offsetWidth}X${this.video.offsetHeight}`,
            videoAreaSize: `${playerEl?.offsetWidth}X${playerEl?.offsetHeight}`,
            metricsVisible: this.checkIsVideoVisibleToEndUser(),
          },
          session: {
            username: user?.username || '',
            roomname: profileName,
            currentSessionDuration: (new Date().getTime() - this.sessionStarted.getTime()) / SECOND_AS_MILLISECONDS,
            sessionVisible: !document.hidden,
          },
        };
        sendVideoMetrics(metricsData);
      }, SEND_VIDEO_METRICS_DELAY);
  };

  handleVideoHeight = () => {
    const { temporaryPlayerService } = this.props;

    if (!temporaryPlayerService.isInFullscreen()) {
      this.determineVideoHeight();
    }
  };

  determineVideoHeight = () => {
    const {
      isDesktop,
      temporaryPlayerService,
      updateVideoMetrics,
    } = this.props;

    const { showPip } = this.state;

    if (isDesktop && !showPip && !temporaryPlayerService.isInFullscreen()) {
      updateVideoMetrics({
        width: this.video.offsetWidth,
        height: this.video.offsetHeight,
      });

      const profileVideoWrap = document.querySelector('#profileVideoWrap');
      const videoInteractSectionWrapper = document.querySelector('#videoInteractSectionWrapper');
      const determinedVideoHeight = profileVideoWrap.offsetHeight - videoInteractSectionWrapper.offsetHeight;

      this.setState({ determinedVideoHeight });
      this.video.style.maxHeight = `${determinedVideoHeight}px`;
      this.video.style.height = `${determinedVideoHeight}px`;
    } else if (ProfileStore.deviceInLandscape) {
      this.setState((prevState) => {
        return { forceRerender: !prevState.forceRerender };
      });
    }
  };

  removeVideoMaxHeight = () => {
    if (this.props.isDesktop) {
      this.video.style.maxHeight = null;
    }
  };

  removeVideoHeight = () => {
    if (this.props.isDesktop) {
      this.video.style.height = null;
    }
  };

  startVideoAds = () => {
    const {
      shouldShowVideoAds,
      temporaryMessagingService,
    } = this.props;

    // TODO this method not moved to player facade because will be deprecated at all
    temporaryMessagingService.emit(HLS_DEBUG, { method: 'player::startVideoAds' });

    if (shouldShowVideoAds) {
      VideoAdService.start(
        this.video,
        this.setVideoPlayerRef.current
      );
    }
  };

  handlePip = () => {
    const { shouldShowVideoAds } = this.props;
    const { previousScroll, videoHeightForScroll } = this.state;
    const bannerHeight = 76;

    const playerContainerHeight = this.setVideoPlayerRef.current.getBoundingClientRect().height;

    if (playerContainerHeight && videoHeightForScroll !== playerContainerHeight) {
      this.setState({ videoHeightForScroll: playerContainerHeight });
    }

    if (window.pageYOffset >= previousScroll && (window.pageYOffset >= videoHeightForScroll + bannerHeight)) {
      if (shouldShowVideoAds && !VideoAdService.isAdOver) {
        return;
      }
      this.removeVideoHeight();
      this.setState({ showPip: true, videoHeight: playerContainerHeight });
    }

    if (window.pageYOffset < previousScroll && (window.pageYOffset <= (videoHeightForScroll + bannerHeight))) {
      this.setState({ showPip: false, closePip: false });
      this.determineVideoHeight();
    }

    if (window.pageYOffset !== 0) {
      this.setState({ previousScroll: window.pageYOffset });
    }
  };

  closePip = () => {
    this.setState({ closePip: true });
  };

  handleOrientationChange = () => {
    const {
      offFullScreen,
      onFullScreen,
      temporaryPlayerService,
    } = this.props;

    if (screen.orientation.type.startsWith('landscape')) {
      temporaryPlayerService.enterFullscreen().then(() => {
        setTimeout(() => {
          if (FirebaseStore.isStreamPortrait) {
            screen.orientation.lock('landscape-primary');
          }
          screen.orientation.unlock();
        }, SET_SCREEN_ORIENTATION_DELAY);
        onFullScreen?.();
      });
    } else if (document.fullscreenElement) {
      temporaryPlayerService.exitFullscreen();
      offFullScreen?.();
    }
  };

  getStreamingInfoAndReconnect = (details, reason) => {
    const { reconnect, temporaryMessagingService } = this.props;

    reconnect({ event: { details, reason } });

    // TODO move to this.playerFacade.reconnect
    if (FirebaseStore.isStreamingSaas) {
      if (details === 'manifestParsingError' || details === 'manifestLoadError') {
        this.delayedSetVideoTimer = setTimeout(() => {
          this.setVideo();
        }, SECOND_AS_MILLISECONDS);
      } else {
        temporaryMessagingService.emit(HLS_DEBUG, {
          method: 'player::ignoredGetStreamingInfoAndReconnect',
          details,
          reason,
        });
      }
    } else {
      backOff(() => ProfileStore.waitForStreamingInfo(), {
        jitter: 'full',
        numOfAttempts: 100,
        maxDelay: 10000,
      }).catch((err) => {
        temporaryMessagingService.emit(HLS_STREAMING_URL_FAILED, err.message);
      });
    }
    // else - no need to call HlsService.reconnect() - we get notified when the source changes via firebase->mobx->react
  };

  setVideo = () => {
    const {
      autoPlay,
      profileName,
      source,
      startAutoPlay,
      temporaryMessagingService,
      temporaryPlayerService,
      user,
    } = this.props;

    // TODO events inside this methods
    //  will be moved to facade as part of next tickets
    //  because of complexity and potentially refactoring of that method
    //  so messaging service called directly

    clearInterval(HlsService.currentTimeInterval);
    temporaryMessagingService.emit(HLS_DEBUG, { method: 'player::setVideo', source });
    clearTimeout(this.delayedSetVideoTimer);

    if (!this.video) {
      temporaryMessagingService.emit(HLS_DEBUG, {
        method: 'player::setVideo',
        error: 'NO video element!',
      });

      return;
    }

    if (HlsService.isHlsSupported) {
      temporaryPlayerService.isPlaying = false;
      HlsService.init(source, this.video);
      HlsService.instance.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (autoPlay) {
          startAutoPlay();
        } else {
          temporaryPlayerService.isPlaying = false;
        }
        if (data.levels && data.levels.length > 1) {
          temporaryPlayerService.setStreamQualityOptions(data.levels, isMobile);
        }
      });
      this.collectMetrics();

      HlsService.instance.on(Hls.Events.ERROR, (event, data) => {
        if (process.env.NODE_ENV === 'production') {
          const eventData = {
            details: data.details,
            errorDetails: { details: data.details, fatal: data.fatal, type: data.type },
            event,
            fatal: data.fatal,
            room: profileName,
            sessionUuid: this.sessionUuid,
            type: data.type,
            user: user?.username || '',
          };
          temporaryMessagingService.emit(SEND_HLS_VIDEO_EVENTS, eventData);
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // try to recover network error
              // startLoad method from HLS documentation didnt worked after stream freeze
              track({
                eventname: 'HLS_NETWORK_ERROR',
                value: {
                  playerType: 'SmartXPlayer',
                  fatal: data.fatal,
                  type: data.type,
                  sessionUuid: this.sessionUuid,
                },
                username: user?.username || '',
                room: profileName,
                details: data.details,
              });
              // ex: {details: "manifestParsingError", reason: "no level found in manifest"}
              this.getStreamingInfoAndReconnect(data.details, data.reason);

              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              track({
                eventname: 'HLS_MEDIA_ERROR',
                value: {
                  playerType: 'SmartXPlayer',
                  fatal: data.fatal,
                  type: data.type,
                  sessionUuid: this.sessionUuid,
                },
                username: user?.username || '',
                room: profileName,
                details: data.details,
              });

              logger.error('error handler: fatal media error encountered, try to recoverMediaError');
              HlsService.instance.recoverMediaError();

              break;
            default:
              track({
                eventname: 'HLS_FATAL_ERROR',
                value: {
                  playerType: 'SmartXPlayer',
                  fatal: data.fatal,
                  type: data.type,
                  sessionUuid: this.sessionUuid,
                },
                username: user?.username || '',
                room: profileName,
                details: data.details,
              });

              // cannot recover
              logger.error(`error handler: fatal error: ${data.type}`);

              break;
          }
        }
      });
    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      this.video.src = source;
      HlsService.init(source, this.video);
      if (autoPlay) {
        startAutoPlay();
      } else {
        temporaryPlayerService.isPlaying = false;
      }
    }
  };

  toggleVolume = () => {
    this.props.temporaryPlayerService.toggleMuted();
  };

  collectMetrics = () => {
    this.smartXMetrics = new SmartxPlayerMetrics(HlsService.instance);
    this.handleCustomVideoMetrics();
  };

  unmuteVideoOnce = () => {
    this.props.temporaryPlayerService.unmuteVideoOnce();
  };

  handleToggleFullScreenInPrivateShows = () => {
    const { toggleFullscreen } = ProfileStore;
    const { setPrivateShowVisibilityChat } = ChatStore;
    toggleFullscreen();
    setPrivateShowVisibilityChat(true);
  };

  handleStreamQualityChange = (key) => {
    HlsService.instance.loadLevel = key;
    this.props.temporaryPlayerService.changeStreamQualityOption(key);
  };

  handleFullscreen = (isCustom) => {
    const { temporaryPlayerService } = this.props;

    if (!temporaryPlayerService.isInFullscreen()) {
      temporaryPlayerService.enterFullscreen();
    }
    if (isCustom) {
      if (temporaryPlayerService.customFullscreenActive) {
        temporaryPlayerService.customFullscreenActive = false;
        ProfileStore.setCustomFullscreen(false);
        temporaryPlayerService.exitFullscreen();
      } else {
        temporaryPlayerService.customFullscreenActive = true;
        ProfileStore.setCustomFullscreen(true);
      }
    }
  };

  /**
   *
   * @param {string[]} baseClasses array of css classes
   * @returns {string} base classes w/o portrait button class
   */

  addPortraitButtonClass = (baseClasses) =>
    classNames(baseClasses, { [styles.portraitButton]: FirebaseStore.isStreamPortrait && isMobile });

  toggleMobileControlls = () => {
    this.clearHideIconsTimeout();
    this.setState({ hideVolume: false });
    this.setHideIconsTimeout();
  };

  setHideIconsTimeout = () => {
    this.buttonsHideTimeout = setTimeout(() => {
      this.setState({ hideVolume: true });
    }, HIDE_ICONS_DELAY);
  };

  clearHideIconsTimeout = () => clearTimeout(this.buttonsHideTimeout);

  handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  onEnter = () => {
    const {
      temporaryPlayerService,
      onHover,
    } = this.props;

    onHover?.();
    temporaryPlayerService.toggleControls(true);
  };

  onLeave = () => {
    const {
      temporaryPlayerService,
      offHover,
    } = this.props;

    offHover?.();
    temporaryPlayerService.toggleControls(false);
  };

  renderPipMuteIcons = () => {
    const { temporaryPlayerService } = this.props;
    const { broadcastStatus } = ProfileStore;

    if (broadcastStatus === BROADCAST_STATUS.SPYING) {
      return null;
    }

    if (temporaryPlayerService.isVideoMuted) {
      return (
        <VolumeMute
          className={styles.volumePip}
          onClick={this.toggleVolume}
        />
      );
    }

    return (
      <VolumeUp
        className={styles.volumePip}
        onClick={this.toggleVolume}
      />
    );
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      embedProfileV2,
      handleVideoDoubleClick,
      isDesktop,
      isSfw,
      renderVideoAdElement,
      renderViewerControls,
      showControls,
      shouldShowVideoAds,
      temporaryPlayerService,
      upgradeToGold,
      width,
    } = this.props;

    const {
      activeStreamQualityOption,
      customFullscreenActive,
      isLoading,
      isVideoMuted,
      message,
      play: onPlay,
      showOverlay,
      showQualityOptions,
      showUnmuteOverlay,
      streamQualityOptions,
      volume,
    } = temporaryPlayerService;

    const {
      showPip,
      closePip,
      hideVolume,
    } = this.state;

    const { isStreamPortrait, newShowsState } = FirebaseStore;
    const { showReconnect, isBuffering } = HlsService;
    const { isAdRunning } = VideoAdService;
    const {
      fullscreen,
      broadcastStatus,
      deviceInLandscape,
      isVideoStatsVisible,
      portraitTrueFullScreen,
      togglePortraitTrueFullScreen,
    } = ProfileStore;
    const { isPerformerGoalVisible } = TippingStore;

    const isPortraitMode = isStreamPortrait || fullscreen;
    const isPrivateOrSpyShow = (
      broadcastStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
      broadcastStatus === BROADCAST_STATUS.SPYING
    );
    const isFullScreenIconVisible = (
      !isStreamPortrait &&
      isPrivateOrSpyShow &&
      isMobileRender()
    );
    const preRollNotOver = (
      shouldShowVideoAds &&
      VideoAdService.scriptLoaded &&
      !VideoAdService.isAdOver
    );

    const playButtonClasses = this.addPortraitButtonClass([styles.play]);
    const loadingClasses = this.addPortraitButtonClass([styles.loading]);

    const videoClasses = classNames({
      [styles.video]: isMobile,
      [styles.videoMobileAspectRatio]: isMobile && !isStreamPortrait && !fullscreen,
      [styles.fullscreenVideo]: isMobile && fullscreen,
      [styles.portraitTrueFullScreen]: isMobile && portraitTrueFullScreen && !deviceInLandscape && !embedProfileV2,
      [styles.portraitTrueFullScreenEmbed]: isMobile && portraitTrueFullScreen && !deviceInLandscape && embedProfileV2,
      [styles.portraitVideo]: isMobile && isStreamPortrait,
      [styles.portraitVideoInLandscape]: isMobile && isStreamPortrait && deviceInLandscape,
      [styles.desktopVideoLandscape]: isDesktop && !isStreamPortrait,
      [styles.desktopVideoPortrait]: isDesktop && isStreamPortrait,
      [styles.blur]: isSfw,
      [styles.pip]: showPip && !closePip,
      [styles.pipPortrait]: showPip && !closePip && isStreamPortrait,
      [styles.preAdVideo]: preRollNotOver,
    });

    const showToggleMuteUnmuteButton = (
      !deviceInLandscape &&
      !isDesktop &&
      broadcastStatus !== BROADCAST_STATUS.SPYING
    );
    const showTogglePortraitFullScreen = (
      !deviceInLandscape &&
      !isDesktop &&
      broadcastStatus !== BROADCAST_STATUS.SPYING &&
      broadcastStatus !== BROADCAST_STATUS.INSIDE_PS &&
      broadcastStatus !== BROADCAST_STATUS.ACCEPTED_INSIDE_PS
    );
    const showUnmuteIconDesktop = (
      showUnmuteOverlay &&
      isDesktop &&
      !isLoading &&
      broadcastStatus !== BROADCAST_STATUS.SPYING
    );
    const showStreamQualityControl = !isDesktop && showQualityOptions;

    const wrapperClasses = classNames({
      [styles.SmartxPlayer]: true,
      [styles.portrait]: isPortraitMode && !isDesktop && !portraitTrueFullScreen,
      [styles.customFullscreen]: customFullscreenActive,
      [styles.blackBackground]: isMobile && isStreamPortrait && deviceInLandscape,
    });

    const unmuteButtonWrapper = classNames({
      [styles.unmuteVideoWrapperDesktop]: true,
      [styles.preAdVideo]: preRollNotOver,
    });

    const bufferingWrapperClasses = classNames({
      [styles.bufferingWrapper]: true,
      [styles.isInFullscreen]: isMobile && fullscreen,
    });

    const buttonsContainerClasses = classNames({
      [styles.buttonsContainer]: true,
      [styles.buttonsContainerTheatre]: !ChatStore.isChatShown,
      [styles.hidden]: hideVolume,
      [styles.buttonsContainerFullScreen]:
        (portraitTrueFullScreen && !deviceInLandscape) || (embedProfileV2 && !deviceInLandscape),
    });

    const overlayPlayerClasses = classNames({
      [styles.overlay]: true,
      [styles.portraitTrueFullScreenOverlay]: portraitTrueFullScreen && !deviceInLandscape,
    });

    const controlsVisible = (
      showControls &&
      !preRollNotOver &&
      !showOverlay
    );

    const videoStyle = {};
    if (deviceInLandscape) {
      videoStyle.style = {};
      const height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      videoStyle.style.height = Math.min(height, width);
    }

    if (portraitTrueFullScreen) {
      videoStyle.style = {};
      const height = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      videoStyle.style.height = height;
    }

    const isGoalVisible = (
      isPerformerGoalVisible ||
      newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS
    );

    const DMCABadgeWrapper = classNames({
      [styles.DMCABadgeWrapperDesktop]: isDesktop,
      [styles.DMCABadgeWrapperMobile]: !isDesktop && !deviceInLandscape,
      [styles.DMCABadgeWrapperMobileHide]: (!isDesktop && isGoalVisible) || (!isDesktop && deviceInLandscape),
    });

    return (
      <div
        ref={this.setVideoPlayerRef}
        className={wrapperClasses}
        id="playerWrapper"
        width={width}
        onClick={this.toggleMobileControlls}
        onMouseLeave={this.onLeave}
        onMouseOver={this.onEnter}
      >
        {isVideoStatsVisible && <VideoMetrics />}
        <div className={buttonsContainerClasses}>
          {showToggleMuteUnmuteButton && (
            <div className={styles.volumeButton} onClick={this.toggleVolume}>
              {volume === 0 || isVideoMuted ? <VolumeOffIcon /> : <VolumeUp />}
            </div>
          )}
          {showStreamQualityControl && (
            <StreamQualityOptions
              activeOption={activeStreamQualityOption}
              clearHideIconsTimeout={this.clearHideIconsTimeout}
              isDesktop={isDesktop}
              isInTheatre={!ChatStore.isChatShown}
              options={streamQualityOptions}
              setHideIconsTimeout={this.setHideIconsTimeout}
              onStreamQualityChange={this.handleStreamQualityChange}
            />
          )}
          {showTogglePortraitFullScreen && (
            <div
              className={styles.fullScreenNormalRoomState}
              onClick={togglePortraitTrueFullScreen}
            >
              {!portraitTrueFullScreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
            </div>
          )}
          {isFullScreenIconVisible && !deviceInLandscape && (
            <div
              className={styles.fullScreenPrivateShowIcon}
              id="togglePrivateShowFullScreen"
              onClick={togglePortraitTrueFullScreen}
            >
              {!portraitTrueFullScreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
            </div>
          )}
        </div>
        {fullscreen && !CookieStorageService.get('swipe-animation-shown') && (
          <TouchAppIcon className={styles.touchIcon} />
        )}
        <div className={DMCABadgeWrapper}>
          <img
            alt="DMCABadge"
            className={styles.DMCABadge}
            src={DMCABadge}
          />
        </div>
        <Background videoNode={this.video} />
        <video
          ref={this.setVideoRef}
          className={videoClasses}
          controlsList="nodownload"
          disablePictureInPicture={true}
          playsInline={true}
          onClick={showPip ? this.handleScrollToTop : null}
          onDoubleClick={!showPip ? handleVideoDoubleClick : null}
          onTouchMove={fullscreen ? this.handleVideoSwipe : null}
          {...videoStyle}
        />
        {showUnmuteIconDesktop && (
          <div
            className={unmuteButtonWrapper}
            onClick={this.unmuteVideoOnce}
          >
            <Button
              className={styles.unmuteVideoBtnDesktop}
              id={this.makeId(this.IDs.unmuteButtonDesktop)}
              label={translate('v2profile.video.unmute.video.text')}
            />
          </div>
        )}
        {showReconnect && (
          <div
            className={bufferingWrapperClasses}
            onClick={this.unmuteVideoOnce}
          >
            <span className={styles.bufferingText}>
              <span className={styles.text}>
                {translate('v2profile.reconnecting')}
              </span>
              <LoadingDots />
            </span>
          </div>
        )}
        {renderVideoAdElement()}
        {isAdRunning && (
          <button className={styles.upgradeToGoldButton} onClick={upgradeToGold}>
            {translate('v2profile.video.ads.upgrade.to.gold')}
          </button>
        )}
        {showOverlay && (
          <div className={overlayPlayerClasses}>
            {isLoading ? (
              <CircularProgress className={loadingClasses} />
            ) : (
              <PlayCircleFilled
                className={playButtonClasses}
                onClick={onPlay}
              />
            )}
            {Boolean(message.length) && (
              <p className={styles.message}>
                {message}
              </p>
            )}
          </div>
        )}
        {isBuffering && !showOverlay && (
          <div className={styles.spinnerOverlay} id="bufferingSpinner">
            <CircularProgress className={styles.spinnerLoading} />
          </div>
        )}
        {controlsVisible && renderViewerControls()}
        {showPip && !closePip && (
          <Fragment>
            <CloseIcon
              className={styles.closePip}
              onClick={this.closePip}
            />
            {this.renderPipMuteIcons()}
          </Fragment>
        )}
      </div>
    );
  }
}

export default SmartxPlayer;
